import React, { useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { cn } from '@anytag/shared-utils';
import { Button, Icon } from '@anytag/shared-atoms';
import { VideoItem } from '../../types';

interface VideoListProps {
  videoItems: VideoItem[];
  isAnalyzing: boolean;
  onRemove: (id: string) => void;
  onClearHistory: () => void;
  onFilesAdded: (files: File[]) => void;
  onFileError: (msg: string) => void;
  globalError: string | null;
  processingIndex: number;
  onAnalyze: () => void;
}

const MAX_SIZE = 100 * 1024 * 1024;

export const VideoList: React.FC<VideoListProps> = ({
  videoItems,
  isAnalyzing,
  onRemove,
  onClearHistory,
  onFilesAdded,
  onFileError,
  globalError,
  processingIndex,
  onAnalyze
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        const firstError = fileRejections[0];
        if (firstError.errors[0].code === 'file-too-large') {
          onFileError(`File "${firstError.file.name}" is too large. Max 100MB.`);
        } else if (firstError.errors[0].code === 'file-invalid-type') {
          onFileError(`File "${firstError.file.name}" has invalid type.`);
        } else {
          onFileError(firstError.errors[0].message);
        }
      }
      if (acceptedFiles.length > 0) {
        onFilesAdded(acceptedFiles);
      }
    },
    [onFilesAdded, onFileError]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { 'video/*': [] },
    maxSize: MAX_SIZE,
    noClick: videoItems.length > 0,
    disabled: isAnalyzing
  });

  return (
    <div className="flex h-full flex-col rounded-xl border-base-border border bg-white p-5">
      <h2 className="mb-4 flex flex-shrink-0 items-center justify-between text-base font-bold text-primary">
        <span className="flex items-center gap-2 text-base leading-none">
          <Icon icon="video" variant="black" size={18} className="text-red-60" />
          Target Videos
        </span>
        <div className="flex items-center gap-2">
          {videoItems.some((i) => !i.file) && (
            <Button
              onClick={onClearHistory}
              variant="outline"
              size="sm"
              className="px-px text-xxs"
              title="Clear History"
            />
          )}
          <span className="text-xs text-primary">{videoItems.length}/10</span>
        </div>
      </h2>

      <div
        {...getRootProps()}
        className={cn(
          'mb-4 custom-scrollbar min-h-0 flex-1 space-y-3 overflow-y-auto pr-1 transition-colors outline-none',
          {
            'rounded-lg border-2 border-dashed border-blue-300 bg-blue-50': isDragActive
          }
        )}
      >
        <input {...getInputProps()} />

        {videoItems.length === 0 ? (
          <div className="group flex h-full max-h-48 cursor-pointer flex-col items-center justify-center rounded-lg border-base-border border-2 border-dashed transition-colors hover:border-border-hover">
            <div className="m-2 rounded-full bg-black-10 p-3 transition-transform group-hover:scale-110">
              <svg
                className="h-6 w-6 text-secondary group-hover:text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
            <p className="px-2 text-center text-xs font-medium text-secondary">
              {isDragActive ? 'Drop videos here' : 'Click or drop videos (Max 100MB)'}
            </p>
          </div>
        ) : (
          <>
            {videoItems.map((item) => (
              <div
                key={item.id}
                className={cn('flex items-center gap-3 rounded border-base-border border bg-white p-2 shadow-sm', {
                  'opacity-75': !item.file && !item.frames
                })}
              >
                <div className="relative h-8 w-12 flex-shrink-0 overflow-hidden rounded bg-black-30">
                  {item.thumbnail ? (
                    <img src={item.thumbnail} alt={item.fileName} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-black-30 text-secondary">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-bold text-primary">{item.fileName}</p>
                  <div className="flex items-center gap-2">
                    {item.status === 'EXTRACTING' && (
                      <span className="text-xxs font-medium text-blue-60">
                        Extracting... {Math.round(item.extractionProgress || 0)}%
                      </span>
                    )}
                    {item.status === 'PROCESSING' && (
                      <span className="text-xxs font-medium text-orange-60">Analyzing...</span>
                    )}
                    {item.status === 'DONE' && (
                      <span className="text-green-60 flex items-center gap-1 text-xxs font-bold">
                        <Icon icon="check" size={12} variant="green" /> Analyzed
                      </span>
                    )}
                    {item.status === 'ERROR' && (
                      <span className="flex items-center gap-1 text-xxs font-bold text-red-60">
                        <Icon icon="warning" size={12} variant="red" /> Failed
                      </span>
                    )}
                    {item.status === 'IDLE' && <span className="text-xxs text-secondary">Ready for analysis</span>}
                  </div>

                  {item.status === 'EXTRACTING' && (
                    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-black-10">
                      <div
                        className="h-full bg-blue-60 transition-all duration-300"
                        style={{ width: `${item.extractionProgress}%` }}
                      />
                    </div>
                  )}
                </div>
                {!isAnalyzing && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(item.id);
                    }}
                    className="p-1 text-secondary hover:text-error"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
            {videoItems.length < 10 && (
              <Button
                onClick={open}
                variant="outline"
                className="mt-4 w-full justify-center border-base-border border-dashed text-xs hover:border-border-hover"
                disabled={isAnalyzing}
              >
                + Add more videos (Drop or Click)
              </Button>
            )}
          </>
        )}
      </div>

      <Button
        onClick={onAnalyze}
        disabled={isAnalyzing || !videoItems.some((i) => i.id)}
        loading={isAnalyzing}
        variant="outline" // TODO: Change to ai-gradient button
        className="mt-4 w-full justify-center"
      >
        {isAnalyzing ? (
          <>
            Analyzing {processingIndex > -1 ? processingIndex + 1 : '...'}/{videoItems.filter((i) => !!i.file).length}
          </>
        ) : (
          <>
            <svg className="mr-2 size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
              />
            </svg>
            Analyze now
          </>
        )}
      </Button>

      {globalError && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-50 bg-red-20 p-3 text-sm text-error">
          <svg className="mt-0.5 h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{globalError}</span>
        </div>
      )}
    </div>
  );
};
